<script setup lang="ts">
/**
 * Modification & annulation de commande : autorise le client à modifier ou
 * annuler sa commande, avec un délai de rétractation avant l'heure de
 * retrait — même délai pour tous les jours, ou un délai par jour de retrait.
 * Les deux toggles (allow_modification/allow_cancellation) sont des clés du
 * endpoint générique restaurants/{storeId}/settings (StoreSettingsService),
 * comme confirmé par la doc Swagger UpdateStoreSettingsDto — le mode/délai
 * restent sur GET/PUT restaurants/{storeId}/order-change (apps/server
 * externe). Auto-save débouncé, même pattern que CommerceCollectSlotsSettings.
 */
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  OrderChangeService,
  type TOrderChangeMode,
} from '@/services/order-change.services';
import { StoreSettingsService } from '@/services/store-settings.services';

const props = defineProps<{ storeId: string }>();

const { t } = useI18n({ useScope: 'global' });

type TWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const WD7: TWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const WD_TO_API: Record<TWeekday, string> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};
function wdShort(d: TWeekday): string {
  return t(`collectSlots.weekdayShort.${d}`);
}

const UNIT_OPTIONS = [
  { id: 'minutes' as const, factor: 1, labelKey: 'orderChange.unitMinutes' },
  { id: 'hours' as const, factor: 60, labelKey: 'orderChange.unitHours' },
];
type TUnit = (typeof UNIT_OPTIONS)[number]['id'];

const loading = ref(true);
const loadError = ref('');
const saving = ref(false);
const saveError = ref('');
const saved = ref(false);

// Jours où le magasin accepte des retraits — dérivés des clés de
// leadByWeekday renvoyées par le serveur au chargement.
const activeDays = ref<TWeekday[]>([]);

const cfg = reactive({
  allowModification: true,
  allowCancellation: true,
  mode: 'same_for_all' as TOrderChangeMode,
  leadValue: 2,
  leadUnit: 'hours' as TUnit,
  byDay: {} as Partial<Record<TWeekday, { value: number; unit: TUnit }>>,
});

function unitFactor(unit: TUnit): number {
  return UNIT_OPTIONS.find((u) => u.id === unit)?.factor ?? 1;
}
function minutesToValueUnit(mins: number): { value: number; unit: TUnit } {
  if (mins > 0 && mins % 60 === 0) {
    return { value: mins / 60, unit: 'hours' };
  }
  return { value: mins, unit: 'minutes' };
}
function dayOf(d: TWeekday): { value: number; unit: TUnit } {
  return cfg.byDay[d] ?? (cfg.byDay[d] = { value: 2, unit: 'hours' as TUnit });
}

const autoSaveReady = ref(false);
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

async function loadSettings() {
  loading.value = true;
  loadError.value = '';
  try {
    const [settingsRes, res] = await Promise.all([
      StoreSettingsService.getStoreSettings(props.storeId),
      OrderChangeService.getSettings(props.storeId),
    ]);
    const settingsData = settingsRes.data?.data ?? settingsRes.data;
    const categories = settingsData?.categories ?? [];
    const flatValues: Record<string, unknown> = {};
    for (const cat of categories) {
      for (const s of cat.settings ?? []) flatValues[s.key] = s.value;
    }
    cfg.allowModification = !!flatValues['order_change.allow_modification'];
    cfg.allowCancellation = !!flatValues['order_change.allow_cancellation'];

    const data = res.data.data;
    cfg.mode = data.mode ?? 'same_for_all';

    if (typeof data.leadMinutes === 'number') {
      const { value, unit } = minutesToValueUnit(data.leadMinutes);
      cfg.leadValue = value;
      cfg.leadUnit = unit;
    }

    const byWeekday = data.leadByWeekday ?? {};
    const apiToLocal = Object.fromEntries(
      Object.entries(WD_TO_API).map(([local, api]) => [api, local]),
    ) as Record<string, TWeekday>;
    const active: TWeekday[] = [];
    for (const [apiDay, mins] of Object.entries(byWeekday)) {
      const localDay = apiToLocal[apiDay];
      if (!localDay) continue;
      active.push(localDay);
      cfg.byDay[localDay] = minutesToValueUnit(mins);
    }
    activeDays.value = WD7.filter((d) => active.includes(d));
    if (!activeDays.value.length) activeDays.value = [...WD7];
  } catch {
    loadError.value = t('orderChange.loadError');
  } finally {
    loading.value = false;
  }
}

async function saveSettings() {
  saving.value = true;
  saveError.value = '';
  saved.value = false;
  try {
    const leadMinutesByWeekday: Record<string, number> = {};
    for (const d of activeDays.value) {
      const dv = dayOf(d);
      if (!Number.isFinite(dv.value)) continue;
      leadMinutesByWeekday[WD_TO_API[d]] = dv.value * unitFactor(dv.unit);
    }
    const sameForAllLead =
      cfg.mode === 'same_for_all' && Number.isFinite(cfg.leadValue)
        ? cfg.leadValue * unitFactor(cfg.leadUnit)
        : null;
    await Promise.all([
      StoreSettingsService.updateStoreSettings(props.storeId, {
        'order_change.allow_modification': cfg.allowModification,
        'order_change.allow_cancellation': cfg.allowCancellation,
      }),
      OrderChangeService.updateSettings(props.storeId, {
        mode: cfg.mode,
        allowCancellation: cfg.allowCancellation,
        allowModification: cfg.allowModification,
        leadMinutes: sameForAllLead,
        leadMinutesByWeekday,
      }),
    ]);
    saved.value = true;
    setTimeout(() => {
      saved.value = false;
    }, 2500);
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    saveError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('orderChange.saveError');
  } finally {
    saving.value = false;
  }
}

// Aperçu texte : prochain jour de retrait actif, et l'heure limite qui en
// découle compte tenu du délai configuré — purement illustratif.
function previewFor(d: TWeekday): { day: string; time: string } | null {
  const dv =
    cfg.mode === 'per_day'
      ? dayOf(d)
      : { value: cfg.leadValue, unit: cfg.leadUnit };
  if (!Number.isFinite(dv.value)) return null;
  const leadMin = dv.value * unitFactor(dv.unit);
  const pickupHour = 19 * 60;
  const limitMin = (((pickupHour - leadMin) % 1440) + 1440) % 1440;
  const h = Math.floor(limitMin / 60)
    .toString()
    .padStart(2, '0');
  const m = (limitMin % 60).toString().padStart(2, '0');
  return { day: wdShort(d), time: `${h}h${m === '00' ? '' : m}` };
}

onMounted(async () => {
  await loadSettings();
  autoSaveReady.value = true;
});
watch(
  () => props.storeId,
  async () => {
    autoSaveReady.value = false;
    await loadSettings();
    autoSaveReady.value = true;
  },
);
watch(
  cfg,
  () => {
    if (!autoSaveReady.value) return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => {
      saveSettings();
    }, 600);
  },
  { deep: true },
);

const canEditLead = computed(
  () => cfg.allowModification || cfg.allowCancellation,
);
</script>

<template>
  <div class="cs cs--orderchange">
    <div v-if="loading" class="cs__loading">
      <i class="pi pi-spin pi-spinner" />
      {{ t('orderChange.loading') }}
    </div>
    <div v-else-if="loadError" class="cs__banner cs__banner--error">
      <i class="pi pi-exclamation-triangle" />
      {{ loadError }}
    </div>

    <template v-else>
      <div class="cs__param">
        <p class="cs__sub">{{ t('orderChange.intro') }}</p>

        <div class="cs__types">
          <button
            type="button"
            class="cs__type"
            :class="{ 'cs__type--on': cfg.allowModification }"
            @click="cfg.allowModification = !cfg.allowModification"
          >
            <span class="cs__type-ic"><i class="pi pi-pencil" /></span>
            <span class="cs__type-tx">
              <b>{{ t('orderChange.allowModification') }}</b>
              <span>{{ t('orderChange.allowModificationDesc') }}</span>
            </span>
          </button>
          <button
            type="button"
            class="cs__type"
            :class="{ 'cs__type--on': cfg.allowCancellation }"
            @click="cfg.allowCancellation = !cfg.allowCancellation"
          >
            <span class="cs__type-ic"><i class="pi pi-times-circle" /></span>
            <span class="cs__type-tx">
              <b>{{ t('orderChange.allowCancellation') }}</b>
              <span>{{ t('orderChange.allowCancellationDesc') }}</span>
            </span>
          </button>
        </div>
      </div>

      <div v-if="canEditLead" class="cs__param">
        <div class="cs__sub-lbl">{{ t('orderChange.leadTitle') }}</div>
        <div class="cs__editor">
          <div class="cs__sec">
            <span class="cs__seg">
              <button
                type="button"
                class="cs__seg-opt"
                :class="{ 'cs__seg-opt--on': cfg.mode === 'same_for_all' }"
                @click="cfg.mode = 'same_for_all'"
              >
                {{ t('orderChange.modeSame') }}
              </button>
              <button
                type="button"
                class="cs__seg-opt"
                :class="{ 'cs__seg-opt--on': cfg.mode === 'per_day' }"
                @click="cfg.mode = 'per_day'"
              >
                {{ t('orderChange.modePerDay') }}
              </button>
            </span>

            <!-- Même délai pour tout -->
            <div v-if="cfg.mode === 'same_for_all'" class="cs__oc-samewrap">
              <div class="cs__oc-samerow">
                <span class="cs__lbl">{{ t('orderChange.leadLabel') }}</span>
                <span class="cs__oc-inputgroup">
                  <input
                    v-model.number="cfg.leadValue"
                    type="number"
                    min="0"
                    max="999"
                    class="cs__ninp"
                  />
                  <select v-model="cfg.leadUnit" class="cs__sel-sm">
                    <option v-for="u in UNIT_OPTIONS" :key="u.id" :value="u.id">
                      {{ t(u.labelKey) }}
                    </option>
                  </select>
                </span>
                <span class="cs__muted">{{
                  t('orderChange.beforePickup')
                }}</span>
              </div>
            </div>

            <!-- Délai par jour de retrait -->
            <div v-else class="cs__oc-daylist">
              <div v-for="d in activeDays" :key="d" class="cs__oc-dayrow">
                <b class="cs__oc-daylabel"
                  >{{ t('orderChange.pickupDay') }}
                  {{ wdShort(d).toLowerCase() }}</b
                >
                <span class="cs__oc-inputgroup">
                  <input
                    v-model.number="dayOf(d).value"
                    type="number"
                    min="0"
                    max="999"
                    class="cs__ninp"
                  />
                  <select v-model="dayOf(d).unit" class="cs__sel-sm">
                    <option v-for="u in UNIT_OPTIONS" :key="u.id" :value="u.id">
                      {{ t(u.labelKey) }}
                    </option>
                  </select>
                </span>
                <span class="cs__muted">{{
                  t('orderChange.beforePickup')
                }}</span>
              </div>
            </div>

            <div class="cs__oc-previews">
              <div v-for="d in activeDays" :key="d" class="cs__oc-previewrow">
                <i class="pi pi-circle-fill cs__oc-previewdot" />
                <span v-if="previewFor(d)">
                  «
                  <b>{{ t('orderChange.pickupDay') }}</b>
                  {{ wdShort(d).toLowerCase() }} →
                  {{ t('orderChange.canChangeUntilShort') }}
                  <b>{{ previewFor(d)!.day }} {{ previewFor(d)!.time }}</b>
                  »
                </span>
                <span v-else class="cs__muted">{{
                  t('orderChange.previewPending')
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="saveError" class="cs__banner cs__banner--error">
        <i class="pi pi-exclamation-triangle" />
        {{ saveError }}
      </div>

      <div class="cs__autosave-status">
        <i v-if="saving" class="pi pi-spin pi-spinner" />
        <i v-else-if="saved" class="pi pi-check-circle cs__autosave-ok" />
        <span>{{
          saving
            ? t('collectSlots.saving')
            : saved
              ? t('collectSlots.saved')
              : ''
        }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Mêmes classes .cs__* que CommerceCollectSlotsSettings.vue (styles scoped
   par composant, non partageables) — gardées identiques pour un rendu
   cohérent entre les deux vues plein écran. */
.cs {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cs__param {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cs__param:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.cs__param-h {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-muted);
  margin-bottom: 0.25rem;
}

.cs__types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.625rem;
}
.cs__type {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-card-bg);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.cs__type--on {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.08);
}
.cs__type-ic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
}
.cs__type--on .cs__type-ic {
  background: #ffd60a;
  color: #0b1320;
}
.cs__type-tx {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.8125rem;
}
.cs__type-tx b {
  color: var(--nxt-on-surface);
}
.cs__type-tx span {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}

.cs__editor {
  background: var(--nxt-surface-low);
  border-radius: 0.75rem;
  padding: 0.875rem;
}
.cs__sec {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cs__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.cs__muted {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cs__ninp {
  width: 3.5rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
}
.cs__sel-sm {
  height: 2rem;
  padding: 0 0.5rem;
  margin-left: 0.375rem;
  margin-right: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}

.cs__loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cs__banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
}
.cs__banner--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.cs__autosave-status {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  min-height: 1.25rem;
}
.cs__autosave-ok {
  color: #10b981;
}

.cs__lbl {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}

/* Sélecteur segmenté "Même délai pour tout" / "Délai par jour de retrait" */
.cs__seg {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.2rem;
  border-radius: 0.75rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  width: fit-content;
}
.cs__seg-opt {
  padding: 0.4rem 0.875rem;
  border-radius: 0.55rem;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.cs__seg-opt--on {
  background: #ffd60a;
  color: #0b1320;
  font-weight: 700;
}

.cs__oc-samewrap {
  margin-top: 0.875rem;
}
.cs__oc-samerow {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cs__oc-inputgroup {
  display: inline-flex;
  align-items: center;
}

.cs__oc-daylist {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.875rem;
}
.cs__oc-dayrow {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  padding: 0.625rem 0.75rem;
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
}
.cs__oc-daylabel {
  min-width: 6.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
}

.cs__oc-previews {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}
.cs__oc-previewrow {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(15, 98, 254, 0.06);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.cs__oc-previewrow b {
  color: var(--nxt-on-surface);
}
.cs__oc-previewdot {
  font-size: 0.375rem;
  color: #0f62fe;
  flex-shrink: 0;
  transform: translateY(-0.15rem);
}
</style>

<style>
[data-theme='dark'] .cs--orderchange .cs__type,
[data-theme='dark'] .cs--orderchange .cs__ninp,
[data-theme='dark'] .cs--orderchange .cs__sel-sm,
[data-theme='dark'] .cs--orderchange .cs__seg,
[data-theme='dark'] .cs--orderchange .cs__oc-dayrow {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cs--orderchange .cs__type--on {
  background: rgba(255, 214, 10, 0.12);
  border-color: #ffd60a;
}
[data-theme='dark'] .cs--orderchange .cs__seg-opt--on {
  background: rgba(255, 214, 10, 0.85);
  color: #0b1320;
}
[data-theme='dark'] .cs--orderchange .cs__editor {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .cs--orderchange .cs__oc-previewrow {
  background: rgba(15, 98, 254, 0.14);
}
</style>
